/**
 * بناء سياق الجلسة (الهوية + العضويات + الأدوار + النطاقات).
 *
 * يُقرأ مباشرة من المخزن لتجنّب الاستدعاء الدائري (لأن سياسة القراءة نفسها
 * تحتاج السياق). هذا يقابل في PostgreSQL دوال SECURITY DEFINER الصغيرة
 * المستخدمة داخل سياسات RLS، وليست تجاوزًا للسياسات: نقرأ فقط بيانات
 * المستخدم الحالي وعضوياته، لا بيانات شركة أخرى.
 */

import { buildAccessContext, resolveScope, type AccessContext, type ResolvedScope } from '@/domain/access';
import { isPermissionKey, type PermissionKey } from '@/domain/permissions';
import type { Membership, MembershipAccessGrant, MembershipRole, Role, RolePermission, RoleScope, SupervisionRelation } from '@/domain/types';
import { effectivePermissions } from '@/domain/access';
import type { PolicyContext } from '../policy';
import { getActiveSession } from './auth';
import { getLocalDb } from './db';

type Raw = Record<string, unknown> & { id: string };

export interface SessionState {
  userId: string;
  email: string;
  companyId: string | null;
  policy: PolicyContext;
}

export const EMPTY_POLICY_CONTEXT: PolicyContext = {
  userId: null,
  isPlatformOwner: false,
  companyId: null,
  membershipId: null,
  permissions: new Set<PermissionKey>(),
  scope: null,
  access: {
    userId: '',
    isPlatformOwner: false,
    membership: null,
    memberships: [],
  },
};

/**
 * الشركة النشطة: تُؤخذ من الجلسة المحفوظة إن كانت العضوية ما زالت نشطة،
 * وإلا تُستنتج من أول عضوية نشطة. لا تُقبل قيمة الشركة من الواجهة كدليل صلاحية.
 */
let activeCompanyOverride: string | null = null;

export function setActiveCompany(companyId: string | null): void {
  activeCompanyOverride = companyId;
}

export function getActiveCompanyOverride(): string | null {
  return activeCompanyOverride;
}

export async function loadSessionState(): Promise<SessionState | null> {
  const session = await getActiveSession();
  if (!session) return null;

  const db = await getLocalDb();
  const memberships = (await db.getAllFromIndex('memberships', 'userId', session.userId)) as unknown as Membership[];
  const owners = (await db.getAllFromIndex('platform_owners', 'userId', session.userId)) as unknown as Raw[];
  const isPlatformOwner = owners.some((o) => o['revokedAt'] === null || o['revokedAt'] === undefined);
  const platformMember = (await db.get('platform_members', session.userId)) as unknown as Raw | undefined;
  const platformPermissions = platformMember?.['status'] === 'active'
    ? ((await db.getAllFromIndex('platform_member_permissions', 'userId', session.userId)) as unknown as Raw[])
        .map((row) => row['permissionKey'])
        .filter((permission): permission is PermissionKey => typeof permission === 'string' && isPermissionKey(permission))
    : [];

  const companies = (await db.getAllFromIndex('companies', 'status', 'active')) as unknown as Raw[];
  const availableCompanies = new Set(
    companies.filter((company) => !company['deletedAt']).map((company) => company.id),
  );
  const activeMemberships = memberships.filter(
    (m) => m.status === 'active' && !m.deletedAt && availableCompanies.has(m.companyId),
  );
  const preferred =
    (activeCompanyOverride && activeMemberships.find((m) => m.companyId === activeCompanyOverride)) ||
    activeMemberships[0] ||
    null;
  const companyId = preferred?.companyId ?? null;

  const membershipIds = activeMemberships.map((m) => m.id);
  const membershipRoles: MembershipRole[] = [];
  for (const membershipId of membershipIds) {
    const rows = (await db.getAllFromIndex('membership_roles', 'membershipId', membershipId)) as unknown as MembershipRole[];
    membershipRoles.push(...rows);
  }

  const roleIds = [...new Set(membershipRoles.map((mr) => mr.roleId))];
  const roles: Role[] = [];
  const rolePermissions: RolePermission[] = [];
  const roleScopes: RoleScope[] = [];
  for (const roleId of roleIds) {
    const role = (await db.get('roles', roleId)) as unknown as Role | undefined;
    if (!role || role.deletedAt) continue;
    roles.push(role);
    rolePermissions.push(
      ...((await db.getAllFromIndex('role_permissions', 'roleId', roleId)) as unknown as RolePermission[]),
    );
    roleScopes.push(...((await db.getAllFromIndex('role_scopes', 'roleId', roleId)) as unknown as RoleScope[]));
  }

  const grants: MembershipAccessGrant[] = [];
  const supervision: SupervisionRelation[] = [];
  if (companyId) {
    grants.push(
      ...((await db.getAllFromIndex('membership_access_grants', 'companyId', companyId)) as unknown as MembershipAccessGrant[]).filter(
        (g) => membershipIds.includes(g.membershipId),
      ),
    );
    const allSupervision = (await db.getAllFromIndex(
      'supervision_relations',
      'companyId',
      companyId,
    )) as unknown as SupervisionRelation[];
    supervision.push(...allSupervision.filter((s) => membershipIds.includes(s.supervisorMembershipId)));
  }

  const membershipRoleMap: Record<string, string[]> = {};
  for (const membershipId of membershipIds) {
    membershipRoleMap[membershipId] = membershipRoles
      .filter((mr) => mr.membershipId === membershipId)
      .map((mr) => mr.roleId);
  }

  const access: AccessContext = buildAccessContext({
    userId: session.userId,
    isPlatformOwner,
    platformPermissions,
    memberships: activeMemberships,
    membershipRoleMap,
    roles,
    rolePermissions,
    roleScopes,
    grants,
    supervision: supervision.map((s) => ({
      supervisorMembershipId: s.supervisorMembershipId,
      subordinateMembershipId: s.subordinateMembershipId,
      canViewTasks: s.canViewTasks,
      canAssignTasks: s.canAssignTasks,
      canApproveTasks: s.canApproveTasks,
      canViewReports: s.canViewReports,
      startsAt: s.startsAt,
      endsAt: s.endsAt,
    })),
    companyId,
  });

  let scope: ResolvedScope | null = null;
  if (companyId) {
    const branches = (await db.getAllFromIndex('branches', 'companyId', companyId)) as unknown as Raw[];
    const brands = (await db.getAllFromIndex('brands', 'companyId', companyId)) as unknown as Raw[];
    const links = (await db.getAllFromIndex('brand_branch_links', 'companyId', companyId)) as unknown as Raw[];
    scope = resolveScope({
      ctx: access,
      companyId,
      allBranches: branches
        .filter((b) => !b['deletedAt'])
        .map((b) => ({ id: b.id, brandId: (b['brandId'] as string | null) ?? null })),
      allBrands: brands.filter((b) => !b['deletedAt']).map((b) => ({ id: b.id })),
      brandBranchLinks: links.map((l) => ({
        brandId: l['brandId'] as string,
        branchId: l['branchId'] as string,
      })),
    });
  }

  const permissions = effectivePermissions(access);
  // صلاحيات المنصة لا تُمنح عبر أدوار الشركات، لكن مالك المنصة يحملها من الهوية.
  const policy: PolicyContext = {
    userId: session.userId,
    isPlatformOwner,
    companyId,
    membershipId: access.membership?.membership.id ?? null,
    permissions,
    scope,
    access,
  };

  return { userId: session.userId, email: session.email, companyId, policy };
}

/** مجموعة الصلاحيات الفعّالة للعرض في الواجهة. */
export function permissionList(policy: PolicyContext): PermissionKey[] {
  return [...policy.permissions].filter(isPermissionKey);
}
